import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { type OrderPayboxSubmitParams } from '../type';

interface UsePayboxMessagesProps {
    iframeRef: RefObject<HTMLIFrameElement | null>;
    amount: number;
    onSubmit: (params: OrderPayboxSubmitParams) => void;
    setYaPayDiscount?: (discount?: string) => void;
}

export const usePayboxMessages = (props: UsePayboxMessagesProps) => {
    const { iframeRef, amount, onSubmit, setYaPayDiscount } = props;

    const payboxLoaded = useRef(false);

    const [payboxHeight, setPayboxHeight] = useState(0);

    // При обновлении цены шлем мессадж в paybox
    useEffect(() => {
        iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ type: 'widget-change', value: amount }), '*');
    }, [amount, iframeRef]);

    const messageHandler = useCallback(
        async (message: MessageEvent) => {
            if (message.source !== iframeRef.current?.contentWindow) {
                return;
            }

            const payboxEventData = JSON.parse(message.data);

            if (payboxEventData.type === 'widget-ready') {
                iframeRef.current?.contentWindow?.postMessage(
                    JSON.stringify({ type: 'widget-change', value: amount }),
                    '*'
                );
                payboxLoaded.current = true;
            }

            if (payboxEventData.type === 'widget-resize') {
                if (!payboxLoaded.current) {
                    return;
                }

                const payboxEventDataValue = JSON.parse(payboxEventData.value);

                setPayboxHeight(payboxEventDataValue.value.height);
            }

            if (payboxEventData.type === 'widget-submit') {
                try {
                    await onSubmit(payboxEventData.value);
                } catch (error) {
                    console.error(error);
                } finally {
                    iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ type: 'widget-unlock' }), '*');
                }
            }

            if (payboxEventData.type === 'widget-change' && setYaPayDiscount) {
                const payboxEventDataValue = JSON.parse(payboxEventData.value);

                setYaPayDiscount(payboxEventDataValue.discount.amount);
            }
        },
        [amount, iframeRef, onSubmit, setYaPayDiscount]
    );

    // Обрабатываем сообщения от paybox
    useEffect(() => {
        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [messageHandler]);

    return { payboxLoaded: payboxLoaded.current, payboxHeight };
};
